import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { useTranslation } from '../context/TranslationContext';
import { useFrontendAuth } from '../hooks/useAuth';
import { AccountStatus, StrikeDto } from '../types';
import { translateWithFallback } from '../utils/translateWithFallback';

function normalizeStatus(raw: unknown): AccountStatus {
    if (raw === AccountStatus.OK || raw === 'OK' || raw === 0) return AccountStatus.OK;
    if (raw === AccountStatus.Warning || raw === 'Warning' || raw === 1) return AccountStatus.Warning;
    if (raw === AccountStatus.Suspended || raw === 'Suspended' || raw === 2) return AccountStatus.Suspended;
    if (raw === AccountStatus.Banned || raw === 'Banned' || raw === 3) return AccountStatus.Banned;
    return AccountStatus.OK;
}

/**
 * Account centre = community standing / strikes only.
 * Email preferences live under My account → Edit settings modal (ProfileSettingsPanel).
 */
const AccountCentre = () => {
    const { t } = useTranslation();
    const tr = (key: string, fallback: string) => translateWithFallback(t, key, fallback);
    const { user, isAuthenticated, isAuthReady } = useFrontendAuth();

    if (!isAuthReady || !isAuthenticated) {
        return <LoadingSpinner />;
    }

    const strikes: StrikeDto[] = user?.strikes ?? [];
    const strikeCount = strikes.length;
    // Authoritative account flag from API — NOT "strikeCount >= 3"
    // (unban sets Status = OK while historical strikes remain)
    const accountStatus = normalizeStatus(user?.status);
    const isBanned = accountStatus === AccountStatus.Banned;
    const isSuspended = accountStatus === AccountStatus.Suspended;
    const isRestricted = isBanned || isSuspended;

    const filledOrbs = Math.min(strikeCount, 3);
    const baseColor = isBanned
        ? 'red'
        : isSuspended
          ? 'red'
          : strikeCount === 0
            ? 'green'
            : strikeCount === 1
              ? 'yellow'
              : strikeCount === 2
                ? 'orange'
                : 'orange'; // 3+ strikes but still OK after unban

    return (
        <div className="mx-auto max-w-3xl py-4 sm:py-8">
            <Link
                to="/my-account"
                className="mb-6 inline-flex items-center gap-1 text-[var(--accent-color)] hover:underline"
            >
                <ChevronLeft className="h-4 w-4" aria-hidden />
                {tr('account.hub.back', 'Back to My Account')}
            </Link>

            <h1 className="mb-2 text-3xl font-bold">{tr('account.centre.title', 'Account centre')}</h1>
            <p className="mb-8 text-gray-600">
                {tr('account.centre.subtitle', 'Your standing on the Petito community')}
            </p>

            <div className="rounded-2xl bg-white p-4 shadow-md sm:p-8">
                <h2 className="mb-2 text-xl font-semibold">{tr('account.centre.health', 'Standing')}</h2>
                <p className="mb-6 text-center text-sm text-gray-600">
                    {tr('account.centre.currentStatus', 'Current status')}:{' '}
                    <strong className="text-[var(--text-color)]">
                        {isBanned
                            ? tr('account.status.label.banned', 'Banned')
                            : isSuspended
                              ? tr('account.status.label.suspended', 'Suspended')
                              : tr('account.status.label.ok', 'Active')}
                    </strong>
                    {strikeCount > 0 && (
                        <span className="text-gray-500">
                            {' '}
                            · {strikeCount} {tr('account.centre.strikesOnRecord', 'strike(s) on record')}
                        </span>
                    )}
                </p>

                <div className="mb-6 flex items-center justify-center gap-3 sm:gap-6">
                    {Array.from({ length: 3 }).map((_, i) => {
                        const isFilled = i < filledOrbs;
                        let filledClass = '';
                        if (baseColor === 'green') {
                            filledClass = 'bg-gradient-to-br from-green-500 to-green-700 ring-green-400';
                        } else if (baseColor === 'yellow') {
                            filledClass = 'bg-gradient-to-br from-yellow-500 to-yellow-700 ring-yellow-400';
                        } else if (baseColor === 'orange') {
                            filledClass = 'bg-gradient-to-br from-orange-500 to-orange-700 ring-orange-400';
                        } else if (baseColor === 'red') {
                            filledClass = 'bg-gradient-to-br from-red-500 to-red-700 ring-red-400';
                        }

                        const emptyClass =
                            strikeCount === 0
                                ? 'bg-gradient-to-br from-green-300 to-green-500 ring-green-400 opacity-100'
                                : 'bg-gradient-to-br from-gray-300 to-gray-500 ring-gray-400 opacity-70';

                        return (
                            <div
                                key={i}
                                className={`relative flex h-12 w-12 sm:h-16 sm:w-16 items-center justify-center rounded-full shadow-xl ring-4 ring-offset-2 sm:ring-offset-4 ring-opacity-70 transition-all duration-500 ${isFilled ? filledClass : emptyClass}`}
                            >
                                <div className="pointer-events-none absolute inset-0 rounded-full bg-white opacity-20 mix-blend-screen blur-md" />
                                {isFilled && (
                                    <span className="text-4xl font-bold text-white opacity-80">
                                        {isRestricted ? '🔒' : '⚡'}
                                    </span>
                                )}
                                {!isFilled && (
                                    <span
                                        className={`text-4xl font-bold ${strikeCount === 0 ? 'text-white opacity-80' : 'text-gray-500 opacity-50'}`}
                                    >
                                        {strikeCount === 0 ? '✓' : '✖'}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>

                {isBanned ? (
                    <div>
                        <p className="text-center font-bold text-red-700">
                            {tr('account.status.bannedDetail', 'Your account has been banned from the marketplace.')}
                        </p>
                        <p className="mt-2 text-center text-sm text-amber-800">
                            {tr(
                                'account.status.limitedWindDown',
                                'You still have limited access to finish open purchases and sales until they are delivered or completed. You cannot create new listings or start new interactions.'
                            )}
                        </p>
                        <StrikeList strikes={strikes} tr={tr} />
                    </div>
                ) : isSuspended ? (
                    <div>
                        <p className="text-center font-bold text-red-600">
                            {tr(
                                'account.suspended',
                                `Your account is suspended due to ${strikeCount} strikes.`
                            )
                                .replace('{count}', String(strikeCount))
                                .replace('{{count}}', String(strikeCount))}
                        </p>
                        <StrikeList strikes={strikes} tr={tr} />
                    </div>
                ) : strikeCount === 0 ? (
                    <p className="text-center text-green-600">
                        {tr('account.health.goodstanding', 'You are in good standing.')}
                    </p>
                ) : (
                    <div>
                        <p className="text-center text-yellow-700">
                            {tr(
                                'account.strikecount',
                                `You have ${strikeCount} strike(s). Your account is still active.`
                            )
                                .replace('{count}', String(strikeCount))
                                .replace('{{count}}', String(strikeCount))}
                        </p>
                        <p className="mt-2 text-center text-sm text-gray-600">
                            {tr(
                                'account.centre.activeWithStrikes',
                                'Historical strikes remain on record even after access is restored.'
                            )}
                        </p>
                        <StrikeList strikes={strikes} tr={tr} />
                    </div>
                )}

                <div className="mt-8 rounded-xl bg-gray-50 p-4 text-sm text-gray-600">
                    <p>
                        {tr(
                            'account.centre.help',
                            'If you have questions about a strike or suspension, contact support.'
                        )}
                    </p>
                    <Link
                        to="/helpdesk"
                        className="mt-2 inline-block font-medium text-[var(--accent-color)] hover:underline"
                    >
                        {tr('form.profile.helpdesk', 'Helpdesk')}
                    </Link>
                </div>
            </div>
        </div>
    );
};

function StrikeList({
    strikes,
    tr,
}: {
    strikes: StrikeDto[];
    tr: (key: string, fallback: string) => string;
}) {
    if (strikes.length === 0) return null;
    return (
        <ul className="mt-4 list-disc space-y-2 pl-6 text-left">
            {strikes.map((strike) => (
                <li key={strike.id}>
                    {strike.reason}{' '}
                    <span className="text-gray-500">
                        ({tr('account.centre.issued', 'issued')}:{' '}
                        {new Date(strike.created ?? '').toLocaleDateString()})
                    </span>
                </li>
            ))}
        </ul>
    );
}

export default AccountCentre;
